import { useEffect, useRef, useState } from "react";
import { Label } from "@/components/ui/label.tsx";
import { Button } from "@/components/ui/button";
import { Volume2 } from "lucide-react";
import type { ContextType as SbCallsContextType } from "@/lib/sendbird-calls";

interface DeviceTesterProps {
  sbCalls: SbCallsContextType;
  isOpen: boolean;
}

export function DeviceTester({ sbCalls, isOpen }: DeviceTesterProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [micLevel, setMicLevel] = useState(0);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const testAudioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      // Cleanup when tester is not open or component unmounts
      if (mediaStream) {
        mediaStream.getTracks().forEach((track) => track.stop());
        setMediaStream(null);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (testAudioRef.current) {
        testAudioRef.current.pause();
        testAudioRef.current = null;
      }
      setMicLevel(0);
      return;
    }

    // Get selected device IDs
    const videoId = sbCalls.videoInputDeviceInfo.current?.deviceId;
    const audioId = sbCalls.audioInputDeviceInfo.current?.deviceId;

    // Request media only if at least one device is selected
    if (videoId || audioId) {
      navigator.mediaDevices
        .getUserMedia({
          video: videoId
            ? { deviceId: { exact: videoId } }
            : audioId
              ? false
              : true, // Request video if selected, or if no audio is selected (to prompt for general permissions)
          audio: audioId
            ? { deviceId: { exact: audioId } }
            : videoId
              ? false
              : true, // Request audio if selected, or if no video is selected
        })
        .then((stream) => {
          setMediaStream(stream);
          // Camera preview
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
          // Mic volume (only if an audio track exists in the stream)
          if (stream.getAudioTracks().length > 0 && audioId) {
            if (audioContextRef.current) {
              audioContextRef.current.close();
            }
            const audioContext = new window.AudioContext();
            audioContextRef.current = audioContext;
            const source = audioContext.createMediaStreamSource(stream);
            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 512;
            analyser.smoothingTimeConstant = 0.7;
            source.connect(analyser);
            analyserRef.current = analyser;

            const dataArray = new Uint8Array(analyser.frequencyBinCount);
            const update = () => {
              if (!analyserRef.current) return;
              analyserRef.current.getByteFrequencyData(dataArray);
              let sum = 0;
              for (let i = 0; i < dataArray.length; i++) {
                sum += dataArray[i];
              }
              const averageVolume = sum / dataArray.length;
              setMicLevel(averageVolume / 255);
              animationFrameRef.current = requestAnimationFrame(update);
            };
            update();
          } else {
            setMicLevel(0); // No audio track, so no mic level
          }
        })
        .catch(() => {
          setMediaStream(null);
          setMicLevel(0);
        });
    } else {
      // No devices selected, clear previous stream if any
      if (mediaStream) {
        mediaStream.getTracks().forEach((track) => track.stop());
        setMediaStream(null);
      }
      setMicLevel(0);
    }

    // Cleanup on unmount (or when isOpen becomes false - handled by the initial check)
    return () => {
      if (mediaStream) {
        mediaStream.getTracks().forEach((track) => track.stop());
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (testAudioRef.current) {
        testAudioRef.current.pause();
        testAudioRef.current = null;
      }
    };
  }, [
    isOpen,
    sbCalls.videoInputDeviceInfo.current,
    sbCalls.audioInputDeviceInfo.current,
    // mediaStream should not be a dependency here to avoid re-running effect on setMediaStream
  ]);

  const handleTestSpeaker = async () => {
    if (testAudioRef.current && !testAudioRef.current.paused) {
      testAudioRef.current.pause();
      testAudioRef.current.currentTime = 0;
      // Force re-render to update button text
      setTestAudioRefState(null);
      return;
    }

    const audio = testAudioRef.current || new Audio("/assets/test-audio.mp3");
    audio.onpause = () => setTestAudioRefState(null); // Update button text when paused
    audio.onended = () => {
      // Update button text when ended
      audio.currentTime = 0;
      setTestAudioRefState(null);
    };

    testAudioRef.current = audio;
    try {
      const speakerId = sbCalls.audioOutputDeviceInfo.current?.deviceId;
      if (
        speakerId &&
        typeof (
          audio as HTMLAudioElement & {
            setSinkId?: (id: string) => Promise<void>;
          }
        ).setSinkId === "function"
      ) {
        await (
          audio as HTMLAudioElement & {
            setSinkId: (id: string) => Promise<void>;
          }
        ).setSinkId(speakerId);
      }
      await audio.play();
      // Force re-render to update button text
      setTestAudioRefState(audio);
    } catch (err) {
      console.error("Failed to play test sound or set sink ID:", err);
    }
  };

  // Dummy state to force re-render for speaker button text
  const [, setTestAudioRefState] = useState<HTMLAudioElement | null>(null);

  if (!isOpen) {
    return null; // Only render when the accordion is open
  }

  const noCameraSelected = !sbCalls.videoInputDeviceInfo.current;
  const noMicSelected = !sbCalls.audioInputDeviceInfo.current;
  const showDeviceSelectionMessage =
    noCameraSelected && noMicSelected && !mediaStream;
  const showAccessErrorMessage =
    !mediaStream &&
    (sbCalls.videoInputDeviceInfo.current ||
      sbCalls.audioInputDeviceInfo.current);

  return (
    <div className="px-1 pb-4 pt-2">
      {showDeviceSelectionMessage && (
        <p className="mb-3 text-sm text-orange-600">
          No camera or microphone selected. Please select a device above to
          start testing.
        </p>
      )}
      {showAccessErrorMessage && (
        <p className="mb-3 text-sm text-red-600">
          Could not access the selected camera/microphone. Please ensure
          permissions are granted and the devices are not in use by another
          application.
        </p>
      )}
      <div className="space-y-6">
        {/* Camera Preview */}
        <div className="space-y-2">
          <Label htmlFor="camera-preview" className="text-sm font-medium">
            Camera Preview
          </Label>
          <video
            id="camera-preview"
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="aspect-video h-48 w-full rounded-lg border bg-gray-900 object-cover"
            style={{ background: "#111827" }}
          />
          <p className="text-muted-foreground text-xs">
            {sbCalls.videoInputDeviceInfo.current?.label ||
              "No camera selected"}
          </p>
        </div>

        {/* Mic Volume Bar */}
        <div className="space-y-2">
          <Label htmlFor="mic-level" className="text-sm font-medium">
            Microphone Input Level
          </Label>
          <div className="flex h-8 w-full items-center overflow-hidden rounded-lg bg-gray-200 px-1">
            <div
              id="mic-level"
              className="h-5 rounded bg-gradient-to-r from-green-400 to-green-600 transition-all duration-100 ease-out"
              style={{
                width: `${Math.min(100, Math.max(0, micLevel * 200))}%`,
              }}
            />
          </div>
          <p className="text-muted-foreground text-xs">
            {sbCalls.audioInputDeviceInfo.current?.label ||
              "No microphone selected"}
          </p>
        </div>

        {/* Speaker Test */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Speaker Test</Label>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleTestSpeaker}
              className="flex items-center gap-2"
            >
              <Volume2 className="h-4 w-4" />
              <span>
                {testAudioRef.current && !testAudioRef.current.paused
                  ? "Stop Test Sound"
                  : "Play Test Sound"}
              </span>
            </Button>
            <p className="text-muted-foreground text-xs">
              {sbCalls.audioOutputDeviceInfo.current?.label ||
                "No speaker selected. Sound will play on default device."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
